import { useEffect, useState } from "react";
import "./CardGallery.scss";

export interface GalleryItem {
    immagini: string[];
    cliente: string;
    data: string;
}

interface CardProps {
    item: GalleryItem;
}

function getCardType(total: number): string {
    if (total === 1) return "count-1";
    if (total === 2) return "count-2";
    if (total === 3) return "count-3";
    if (total === 4) return "count-4";

    return "count-more";
}

export default function Card({ item }: CardProps) {
    const [selectedItem, setSelectedItem] =
        useState<GalleryItem | null>(null);

    const [currentIndex, setCurrentIndex] = useState(0);

    const [touchStartX, setTouchStartX] =
        useState<number | null>(null);

    const total = item.immagini.length;

    const visibleImages = item.immagini.slice(0, 4);

    const currentImages = selectedItem?.immagini ?? [];

    const openLightbox = () => {
        setSelectedItem(item);
        setCurrentIndex(0);
    };

    const closeLightbox = () => {
        setSelectedItem(null);
        setCurrentIndex(0);
    };

    const nextImage = () => {
        if (currentImages.length <= 1) return;

        setCurrentIndex((previous) =>
            (previous + 1) % currentImages.length
        );
    };

    const previousImage = () => {
        if (currentImages.length <= 1) return;

        setCurrentIndex((previous) =>
            (previous - 1 + currentImages.length) %
            currentImages.length
        );
    };

    /*
     * Blocca lo scroll quando il lightbox è aperto
     */
    useEffect(() => {
        if (!selectedItem) {
            document.body.style.overflow = "";
            return;
        }

        document.body.style.overflow = "hidden";

        return () => {
            document.body.style.overflow = "";
        };
    }, [selectedItem]);

    /*
     * Tastiera
     */
    useEffect(() => {
        if (!selectedItem) return;

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                closeLightbox();
            }

            if (event.key === "ArrowRight") {
                nextImage();
            }

            if (event.key === "ArrowLeft") {
                previousImage();
            }
        };

        document.addEventListener("keydown", handleKeyDown);

        return () => {
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [selectedItem, currentImages.length]);

    /*
     * Swipe mobile
     */
    const handleTouchStart = (
        event: React.TouchEvent<HTMLDivElement>
    ) => {
        setTouchStartX(event.changedTouches[0].screenX);
    };

    const handleTouchEnd = (
        event: React.TouchEvent<HTMLDivElement>
    ) => {
        if (touchStartX === null) return;

        const touchEndX = event.changedTouches[0].screenX;
        const difference = touchStartX - touchEndX;

        if (difference > 50) {
            nextImage();
        }

        if (difference < -50) {
            previousImage();
        }

        setTouchStartX(null);
    };

    if (total === 0) return null;

    return (
        <>
            {/* CARD */}
            <div className="card-container">
                <div
                    className={`card ${getCardType(total)}`}
                    onClick={openLightbox}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(event) => {
                        if (
                            event.key === "Enter" ||
                            event.key === " "
                        ) {
                            openLightbox();
                        }
                    }}
                >
                    {visibleImages.map((src, imageIndex) => (
                        <img
                            key={`${src}-${imageIndex}`}
                            src={src}
                            alt={`${item.cliente} - immagine ${
                                imageIndex + 1
                            }`}
                        />
                    ))}

                    {total > 4 && (
                        <div className="more">
                            +{total - 4}
                        </div>
                    )}
                </div>

                <div className="card-info">
                    <div className="cliente">
                        {item.cliente}
                    </div>

                    <div className="data">
                        {item.data}
                    </div>
                </div>
            </div>

            {/* LIGHTBOX */}
            {selectedItem && (
                <div
                    className="lightbox open"
                    onClick={(event) => {
                        if (event.target === event.currentTarget) {
                            closeLightbox();
                        }
                    }}
                    onTouchStart={handleTouchStart}
                    onTouchEnd={handleTouchEnd}
                >
                    <div className="lightbox-content">
                        <div className="lightbox-info">
                            <div className="lightbox-cliente">
                                {selectedItem.cliente}
                            </div>

                            <div className="lightbox-data">
                                {selectedItem.data}
                            </div>
                        </div>

                        <button
                            type="button"
                            className="lightbox-close"
                            onClick={closeLightbox}
                            aria-label="Chiudi"
                        >
                            ×
                        </button>

                        {currentImages.length > 1 && (
                            <button
                                type="button"
                                className="lightbox-arrow arrow-left"
                                onClick={previousImage}
                                aria-label="Immagine precedente"
                            >
                                ‹
                            </button>
                        )}

                        <img
                            key={currentImages[currentIndex]}
                            className="lightbox-image"
                            src={currentImages[currentIndex]}
                            alt={`${selectedItem.cliente} - immagine ${
                                currentIndex + 1
                            }`}
                        />

                        {currentImages.length > 1 && (
                            <button
                                type="button"
                                className="lightbox-arrow arrow-right"
                                onClick={nextImage}
                                aria-label="Immagine successiva"
                            >
                                ›
                            </button>
                        )}

                        <div className="counter">
                            {currentIndex + 1} / {currentImages.length}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}